import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { Anchor, Box, Stack } from '@atlaskit/primitives/compiled';
import DynamicTable from '@atlaskit/dynamic-table';
import type { HeadType, RowType } from '@atlaskit/dynamic-table/types';

import type { ContentReportTableItem } from '../contentReportTableTypes.ts';
import { i18n } from '../contentReportTableMacroI18n.ts';

import { CreatorCell } from './CreatorCell.tsx';
import { SocialCell } from './SocialCell.tsx';

interface ContentReportTableProps {
	items: ContentReportTableItem[];
	hasSocialColumn: boolean;
	showCommentsCount: boolean;
	showLikesCount: boolean;
	canViewProfiles: boolean;
	showMoreResultsUrl: string | null;
	macroId?: string;
}

export const ContentReportTable = ({
	items,
	hasSocialColumn,
	showCommentsCount,
	showLikesCount,
	canViewProfiles,
	showMoreResultsUrl,
	macroId,
}: ContentReportTableProps) => {
	const head: HeadType = useMemo(
		() => ({
			cells: [
				{
					key: 'title',
					content: <FormattedMessage {...i18n.headingTitle} />,
				},
				{
					key: 'creator',
					content: <FormattedMessage {...i18n.headingCreator} />,
				},
				{
					key: 'modified',
					content: <FormattedMessage {...i18n.headingModified} />,
				},
				...(hasSocialColumn
					? [
							{
								key: 'social',
								content: <FormattedMessage {...i18n.headingSocial} />,
							},
						]
					: []),
			],
		}),
		[hasSocialColumn],
	);

	const rows: RowType[] = useMemo(
		() =>
			items.map((item) => ({
				key: item.id,
				testId: `content-report-row-${item.id}`,
				cells: [
					{
						key: 'title',
						content: (
							<Anchor href={item.url} target="_blank" rel="noopener noreferrer">
								{item.title}
							</Anchor>
						),
					},
					{
						key: 'creator',
						content: (
							<CreatorCell
								accountId={item.creatorAccountId}
								displayName={item.creatorDisplayName}
								canViewProfiles={canViewProfiles}
							/>
						),
					},
					{
						key: 'modified',
						content: item.lastModified,
					},
					...(hasSocialColumn
						? [
								{
									key: 'social',
									content: (
										<SocialCell
											commentCount={item.commentCount}
											likeCount={item.likeCount}
											showCommentsCount={showCommentsCount}
											showLikesCount={showLikesCount}
										/>
									),
								},
							]
						: []),
				],
			})),
		[items, hasSocialColumn, showCommentsCount, showLikesCount, canViewProfiles],
	);

	return (
		<Stack space="space.0" testId="content-report-table-macro">
			<Box data-fabric-macro={macroId} data-macro-body="" testId="content-report-table">
				<DynamicTable head={head} rows={rows} isFixedSize />
			</Box>
			{showMoreResultsUrl && (
				<Box>
					<Anchor
						href={showMoreResultsUrl}
						target="_blank"
						rel="noopener noreferrer"
						testId="content-report-table-more-results"
					>
						<FormattedMessage {...i18n.findMoreResults} />
					</Anchor>
				</Box>
			)}
		</Stack>
	);
};
